import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBriefcase,
  faChartColumn,
  faCheckSquare,
  faClipboardCheck,
  faFilePdf,
  faFilter,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../../components/Ui_Element/CustomToast";
import {
  formatTaskDate,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  toDateInputValue,
} from "../taskUtils";
import {
  fetchAllTaskReport,
  fetchAllTaskReportFilterOptions,
} from "./allTasksReportApiService";
import { openAllTasksReportPdf } from "./allTasksReportPdfService";

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 0);
  return toDateInputValue(date);
};

const emptyReport = {
  items: [],
  statusCounts: [],
  priorityCounts: [],
  totalTasks: 0,
};

const COLUMN_OPTIONS = [
  { key: "department", label: "Department" },
  { key: "employee", label: "Employee" },
  { key: "task", label: "Task" },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "activity", label: "Last Activity" },
  { key: "actions", label: "Actions" },
];

const DEFAULT_SELECTED_COLUMNS = COLUMN_OPTIONS.map((item) => item.key);

const SEL =
  "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:focus:border-amber-500 disabled:bg-slate-50 dark:disabled:bg-slate-700/50";

function StatCard({ label, value, cls }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${cls}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </div>
    </div>
  );
}

function ReportField({ label, required = false, children, helper }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {helper ? (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{helper}</div>
      ) : null}
    </div>
  );
}

function ColumnMultiSelect({
  options,
  selectedValues,
  onToggle,
  onSelectAll,
  onUnselectAll,
  isOpen,
  onOpenChange,
}) {
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.key))
    .map((option) => option.label)
    .join(", ");

  useEffect(() => {
    if (!isOpen) return undefined;

    const updateMenuPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const estimatedMenuHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const shouldOpenUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);

      setMenuStyle({
        position: "fixed",
        top: shouldOpenUp ? "auto" : rect.bottom + 8,
        bottom: shouldOpenUp ? window.innerHeight - rect.top + 8 : "auto",
        left: Math.max(8, rect.right - width),
        width,
        zIndex: 9999,
      });
    };

    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        onOpenChange(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative z-10">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-600"
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {selectedLabels || "Select columns"}
        </span>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {selectedValues.length} selected
        </span>
      </button>

      {isOpen && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 px-2 pb-2 dark:border-slate-700">
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={onUnselectAll}
                  className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Unselect All
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto overscroll-contain">
                {options.map((option) => {
                  const checked = selectedValues.includes(option.key);

                  return (
                    <label
                      key={option.key}
                      className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggle(option.key)}
                        disabled={selectedValues.length === 1 && checked}
                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400 dark:border-slate-600"
                      />
                      <span className="min-w-0 flex-1 break-words">{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default function AllTasksReportPage() {
  const location = useLocation();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [priorityOptions, setPriorityOptions] = useState(["All"]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(emptyReport);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED_COLUMNS);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    departmentId: "",
    assignedEmployeeId: "",
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    status: "All",
    priority: "All",
  });

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      setFiltersLoading(true);

      try {
        const result = await fetchAllTaskReportFilterOptions();
        if (cancelled) return;

        setDepartments(result.departments || []);
        setEmployees(result.employees || []);
        setStatusOptions(["All", ...(result.statuses || [])]);
        setPriorityOptions(["All", ...(result.priorities || [])]);
      } catch {
        if (!cancelled) {
          setError("All task report filters could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setFiltersLoading(false);
        }
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedDepartment = useMemo(
    () =>
      departments.find(
        (item) => Number(item.id) === Number(filters.departmentId || 0)
      ) || null,
    [departments, filters.departmentId]
  );

  const visibleEmployees = useMemo(() => {
    if (!filters.departmentId) return employees;
    return employees.filter(
      (item) => Number(item.departmentId || 0) === Number(filters.departmentId)
    );
  }, [employees, filters.departmentId]);

  const selectedEmployee = useMemo(
    () =>
      employees.find(
        (item) => Number(item.id) === Number(filters.assignedEmployeeId || 0)
      ) || null,
    [employees, filters.assignedEmployeeId]
  );

  useEffect(() => {
    if (
      filters.assignedEmployeeId &&
      !visibleEmployees.some(
        (item) => Number(item.id) === Number(filters.assignedEmployeeId)
      )
    ) {
      setFilters((current) => ({ ...current, assignedEmployeeId: "" }));
    }
  }, [filters.assignedEmployeeId, visibleEmployees]);

  const statusCounts = useMemo(
    () =>
      report.statusCounts.reduce((result, item) => {
        result[item.status] = Number(item.count || 0);
        return result;
      }, {}),
    [report.statusCounts]
  );

  const priorityCounts = useMemo(
    () =>
      report.priorityCounts.reduce((result, item) => {
        result[item.priority] = Number(item.count || 0);
        return result;
      }, {}),
    [report.priorityCounts]
  );

  const reportCompanyInfo = useMemo(
    () => ({
      name: "BanglaFire CRM",
      code: "Support Desk",
      email: "support@banglafire.com",
      phone: "",
      website: "www.banglafire.com",
      address: "BanglaFire CRM Support Desk",
    }),
    []
  );

  const reportFilterSummary = useMemo(
    () => ({
      departmentName: selectedDepartment?.name || "All Departments",
      employeeName:
        selectedEmployee?.fullName ||
        selectedEmployee?.workEmail ||
        "All Employees",
      startDate: filters.startDate,
      endDate: filters.endDate,
      status: filters.status === "All" ? "All Statuses" : filters.status,
      priority: filters.priority === "All" ? "All Priorities" : filters.priority,
    }),
    [
      filters.endDate,
      filters.priority,
      filters.startDate,
      filters.status,
      selectedDepartment,
      selectedEmployee,
    ]
  );

  const handleFilterChange = (field, value) => {
    setFilters((current) => {
      if (field === "departmentId") {
        return {
          ...current,
          departmentId: value,
          assignedEmployeeId: "",
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });
  };

  const validateFilters = () => {
    if (!filters.startDate || !filters.endDate) {
      throw new Error("Start date and end date are required.");
    }

    if (filters.endDate < filters.startDate) {
      throw new Error("End date must be on or after start date.");
    }
  };

  const handleSearch = async () => {
    try {
      validateFilters();
    } catch (validationError) {
      const message =
        validationError?.message || "Please review the report filters.";
      setError(message);
      showToast.error(message);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await fetchAllTaskReport({
        departmentId: filters.departmentId,
        assignedEmployeeId: filters.assignedEmployeeId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status === "All" ? "" : filters.status,
        priority: filters.priority === "All" ? "" : filters.priority,
      });

      setReport(result);
      setSearched(true);
    } catch (requestError) {
      const message =
        requestError?.message || "All task report could not be loaded.";
      setError(message);
      setReport(emptyReport);
      setSearched(true);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);

    try {
      await openAllTasksReportPdf({
        companyInfo: reportCompanyInfo,
        filters: reportFilterSummary,
        report,
        selectedColumns,
      });
    } catch (exportError) {
      const message =
        exportError?.message || "PDF report could not be generated.";
      showToast.error(message);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((current) => {
      if (current.includes(columnKey)) {
        if (current.length === 1) {
          return current;
        }

        return current.filter((item) => item !== columnKey);
      }

      return [...current, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
  };

  const handleUnselectAllColumns = () => {
    setSelectedColumns([DEFAULT_SELECTED_COLUMNS[0]]);
  };

  const isColumnVisible = (columnKey) => selectedColumns.includes(columnKey);

  return (
    <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950 md:p-6">
      <Breadcrumb pageName="All Task Report" currentPage="All Task Report" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-xl dark:border-gray-800 dark:bg-[#161B22]" style={{ boxShadow: "0 16px 48px rgba(192,76,42,0.10)" }}>
        {/* Hero Header */}
        <div className="relative overflow-hidden px-5 py-6 text-white md:px-7 md:py-8" style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}>
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-8 -translate-y-8 rounded-full bg-white/10 blur-3xl" />
          <div className="h-[3px] absolute bottom-0 left-0 right-0" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-orange-100 backdrop-blur-sm">
                <FontAwesomeIcon icon={faChartColumn} />
                All Employee Task Reporting
              </div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
                Department, employee, date, status, and priority wise task report
              </h1>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-orange-100">
                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
                  All task report workspace
                </span>
                {selectedDepartment ? (
                  <span className="rounded-full border border-white/25 bg-white/15 px-3 py-1 backdrop-blur-sm">
                    Department: {selectedDepartment.name}
                  </span>
                ) : null}
                {filters.status !== "All" ? (
                  <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
                    Status: {filters.status}
                  </span>
                ) : null}
                {filters.priority !== "All" ? (
                  <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
                    Priority: {filters.priority}
                  </span>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={loading || filtersLoading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-70"
              style={{ color: "#C04C2A" }}
            >
              <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
              {loading ? "Loading Report..." : "Run Report"}
            </button>
          </div>
        </div>

        <div className="space-y-6 p-5 md:p-6">
          {/* Filters Card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Filters
              </h2>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}>
                <FontAwesomeIcon icon={faFilter} />
              </div>
            </div>

            {error ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
                {error}
              </div>
            ) : null}

            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <ReportField label="Department">
                <select
                  value={filters.departmentId}
                  onChange={(event) =>
                    handleFilterChange("departmentId", event.target.value)
                  }
                  disabled={filtersLoading}
                  className={SEL}
                >
                  <option value="">All Departments</option>
                  {departments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField
                label="Employee"
                helper={
                  filters.departmentId ? "Filtered by selected department" : ""
                }
              >
                <select
                  value={filters.assignedEmployeeId}
                  onChange={(event) =>
                    handleFilterChange("assignedEmployeeId", event.target.value)
                  }
                  disabled={filtersLoading}
                  className={SEL}
                >
                  <option value="">All Employees</option>
                  {visibleEmployees.map((item) => (
                    <option key={item.id || item.userId} value={item.id}>
                      {item.fullName || item.workEmail || item.userId}
                      {item.employeeCode
                        ? ` (${item.employeeCode})`
                        : item.employeeNumber
                        ? ` (${item.employeeNumber})`
                        : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Status">
                <select
                  value={filters.status}
                  onChange={(event) =>
                    handleFilterChange("status", event.target.value)
                  }
                  disabled={filtersLoading}
                  className={SEL}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status === "All" ? "All Status" : status}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Priority">
                <select
                  value={filters.priority}
                  onChange={(event) =>
                    handleFilterChange("priority", event.target.value)
                  }
                  disabled={filtersLoading}
                  className={SEL}
                >
                  {priorityOptions.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority === "All" ? "All Priorities" : priority}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Start Date" required>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) =>
                    handleFilterChange("startDate", event.target.value)
                  }
                  className={SEL}
                />
              </ReportField>

              <ReportField label="End Date" required>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) =>
                    handleFilterChange("endDate", event.target.value)
                  }
                  className={SEL}
                />
              </ReportField>

              <div className="flex items-end gap-3 sm:col-span-2 lg:col-span-3">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      departmentId: "",
                      assignedEmployeeId: "",
                      startDate: getDefaultStartDate(),
                      endDate: getDefaultEndDate(),
                      status: "All",
                      priority: "All",
                    });
                    setReport(emptyReport);
                    setError("");
                    setSearched(false);
                  }}
                  className="flex-1 rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
                  style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 100%)" }}
                >
                  <FontAwesomeIcon
                    icon={loading ? faSpinner : faSearch}
                    spin={loading}
                  />
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Report Snapshot Card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Report Snapshot
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  See task load by status and priority for the selected filters.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                >
                  <FontAwesomeIcon
                    icon={loading ? faSpinner : faRotate}
                    spin={loading}
                  />
                  Refresh Result
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={loading || filtersLoading || exportingPdf}
                  className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon
                    icon={exportingPdf ? faSpinner : faFilePdf}
                    spin={exportingPdf}
                  />
                  {exportingPdf ? "Preparing PDF..." : "PDF Report"}
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
              <StatCard
                label="Total Tasks"
                value={report.totalTasks || 0}
                cls="border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-700/40"
              />
              <StatCard
                label="Pending"
                value={statusCounts.Pending || 0}
                cls="border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20"
              />
              <StatCard
                label="In Progress"
                value={statusCounts["In Progress"] || 0}
                cls="border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-900/20"
              />
              <StatCard
                label="Completed"
                value={statusCounts.Completed || 0}
                cls="border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-900/20"
              />
              <StatCard
                label="Urgent"
                value={priorityCounts.Urgent || 0}
                cls="border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/20"
              />
              <StatCard
                label="High"
                value={priorityCounts.High || 0}
                cls="border-orange-200 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-900/20"
              />
            </div>
          </div>

          {/* Table Card */}
          <div className="overflow-visible rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  All Task Report Rows
                </h2>
                <div className="flex items-center justify-end gap-2 lg:w-[280px]">
                  <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: "linear-gradient(135deg, #C04C2A 0%, #4A1B13 100%)" }}>
                    <FontAwesomeIcon icon={faCheckSquare} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <ColumnMultiSelect
                      options={COLUMN_OPTIONS}
                      selectedValues={selectedColumns}
                      onToggle={handleColumnToggle}
                      onSelectAll={handleSelectAllColumns}
                      onUnselectAll={handleUnselectAllColumns}
                      isOpen={isColumnMenuOpen}
                      onOpenChange={setIsColumnMenuOpen}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-slate-400">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Loading report rows...
              </div>
            ) : !searched ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-slate-400">
                Run the report to see task rows.
              </div>
            ) : report.items.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left">
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      {isColumnVisible("department") ? (
                        <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">
                          Department
                        </th>
                      ) : null}
                      {isColumnVisible("employee") ? (
                        <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">
                          Employee
                        </th>
                      ) : null}
                      {isColumnVisible("task") ? (
                        <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">
                          Task
                        </th>
                      ) : null}
                      {isColumnVisible("priority") ? (
                        <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">
                          Priority
                        </th>
                      ) : null}
                      {isColumnVisible("status") ? (
                        <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">
                          Status
                        </th>
                      ) : null}
                      {isColumnVisible("activity") ? (
                        <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">
                          Last Activity
                        </th>
                      ) : null}
                      {isColumnVisible("actions") ? (
                        <th className="px-5 py-3 text-right font-semibold text-slate-600 dark:text-slate-300">
                          Actions
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item, index) => (
                      <tr
                        key={item.taskId}
                        className={`border-t border-slate-100 dark:border-slate-700 ${
                          index % 2 === 1
                            ? "bg-slate-50/50 dark:bg-slate-700/20"
                            : ""
                        }`}
                      >
                        {isColumnVisible("department") ? (
                          <td className="px-5 py-3 align-top">
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              <FontAwesomeIcon
                                icon={faBriefcase}
                                style={{ color: "#C04C2A" }}
                              />
                              {item.departmentName || "No Department"}
                            </div>
                          </td>
                        ) : null}
                        {isColumnVisible("employee") ? (
                          <td className="px-5 py-3 align-top">
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {item.assignedEmployeeName || "Unassigned"}
                            </div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {item.assignedEmployeeEmail ||
                                item.assignedUserId ||
                                "No email"}
                            </div>
                          </td>
                        ) : null}
                        {isColumnVisible("task") ? (
                          <td className="px-5 py-3 align-top">
                            <div className="font-semibold text-slate-900 dark:text-slate-100">
                              {item.title}
                            </div>
                            <div className="mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
                              {item.description || "No description"}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400 dark:text-slate-500">
                              <span>
                                Created by {item.createdByName || "Unknown"}
                              </span>
                              <span>
                                Created {formatTaskDate(item.createdAt, true)}
                              </span>
                              <span>Due {formatTaskDate(item.dueDate)}</span>
                            </div>
                            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                              Ticket: {item.ticketNumber || "Manual task"}{" "}
                              {item.ticketSubject
                                ? `- ${item.ticketSubject}`
                                : ""}
                            </div>
                          </td>
                        ) : null}
                        {isColumnVisible("priority") ? (
                          <td className="px-5 py-3 align-top">
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskPriorityClasses(item.priority)}`}
                            >
                              {item.priority}
                            </span>
                          </td>
                        ) : null}
                        {isColumnVisible("status") ? (
                          <td className="px-5 py-3 align-top">
                            <div className="space-y-2">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskStatusClasses(item.status)}`}
                              >
                                {item.status}
                              </span>
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                Progress: {Number(item.progressPercent || 0)}%
                              </div>
                            </div>
                          </td>
                        ) : null}
                        {isColumnVisible("activity") ? (
                          <td className="px-5 py-3 align-top">
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {formatTaskDate(
                                item.lastActivityAt || item.updatedAt,
                                true
                              )}
                            </div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {item.lastActionLabel} by{" "}
                              {item.lastActorName || "Unknown"}
                            </div>
                            {item.lastNote ? (
                              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                                {item.lastNote}
                              </div>
                            ) : null}
                          </td>
                        ) : null}
                        {isColumnVisible("actions") ? (
                          <td className="px-5 py-3 align-top">
                            <div className="flex justify-end gap-2">
                              {item.ticketId ? (
                                <Link
                                  to={`/tickets/${item.ticketId}`}
                                  state={{
                                    backTo: `${location.pathname}${location.search}`,
                                    viewOnly: true,
                                  }}
                                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                                >
                                  <FontAwesomeIcon
                                    icon={faTicket}
                                    style={{ color: "#C04C2A" }}
                                  />
                                  Ticket
                                </Link>
                              ) : null}
                              <Link
                                to="/all-tasks"
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              >
                                <FontAwesomeIcon
                                  icon={faClipboardCheck}
                                  style={{ color: "#C04C2A" }}
                                />
                                Task Desk
                              </Link>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-slate-400">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
                No tasks matched the selected filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
